import { FormEvent, useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "../components/ui/PasswordInput";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import * as authService from "../services/autService";

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast({
        title: t("auth.resetPassword.invalidLinkTitle"),
        description: t("auth.resetPassword.invalidLinkDescription"),
        variant: "destructive",
      });
      navigate("/forgot-password", { replace: true });
    }
  }, [token, navigate, toast, t]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: t("auth.resetPassword.errorTitle"),
        description: t("auth.resetPassword.passwordMismatch"),
        variant: "destructive",
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: t("auth.resetPassword.errorTitle"),
        description: t("auth.resetPassword.passwordTooShort"),
        variant: "destructive",
      });
      return;
    }

    if (!token) {
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword({ token, password });
      toast({
        title: t("auth.resetPassword.successTitle"),
        description: t("auth.resetPassword.successDescription"),
        variant: "success",
      });
      navigate("/login", { replace: true });
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err instanceof Error
          ? err.message
          : t("auth.resetPassword.errorDescription");
      toast({
        title: t("auth.resetPassword.errorTitle"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <section className="mx-auto max-w-md space-y-6 rounded-3xl border border-border bg-card p-8 text-foreground shadow-lg dark:border-white/10 dark:bg-white/5 dark:text-white">
      <div>
        <h2 className="text-2xl font-semibold">{t("auth.resetPassword.title")}</h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t("auth.resetPassword.subtitle")}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.resetPassword.passwordLabel")}</Label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={8}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">{t("auth.resetPassword.confirmPasswordLabel")}</Label>
          <PasswordInput
            id="confirmPassword"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
            minLength={8}
          />
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t("auth.resetPassword.submitting") : t("auth.resetPassword.submit")}
        </Button>
      </form>
      <div className="text-center">
        <Link
          to="/login"
          className="text-sm text-primary underline hover:text-primary/80 dark:text-white/70 dark:hover:text-white"
        >
          {t("auth.resetPassword.backToLogin")}
        </Link>
      </div>
    </section>
  );
};

export default ResetPasswordPage;

